/* Received documents: the three inbound tab states and the inbound stage counts.
   Hand-typed until the inbound lane's schema.d.ts regenerates these (m4-rulings.md #58, #59). */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiGet, apiSend } from "./client";
import type { ErrorClass, Page, StageCounts } from "./types";
import { INBOUND_STAGES } from "../domain/stages";

/** m4-rulings.md #50: the inbound tab states. `landed` is the inbound spelling of `done`;
    `error` is a rejected document, which otherwise sat in no tab (BUG-011). */
export type InboundState = "in_flight" | "held" | "landed" | "error";

/** `GET /api/documents?direction=in` rows. A held row carries `errorCode` but no `errorClass`;
    a rejected one carries both (m4-rulings.md #87). */
export type InboundDocumentRow = {
  id: string;
  entityId: string;
  entityCode: string;
  entityName: string;
  direction: "in";
  docType: string;
  erpDocId: string | null;
  invoiceNumber: string | null;
  uuid: string | null;
  stage: string;
  state: InboundState;
  errorClass: ErrorClass | null;
  errorCode: string | null;
  counterparty: string | null;
  totalAmount: number | null;
  currency: string | null;
  attempts: number;
  createdAt: string;
  updatedAt: string;
};

export type InboundTabCounts = { in_flight: number; held: number; landed: number; error: number };

const FAST_POLL = 5000;
const PAGE_SIZE = 50;

const IN_FLIGHT_STAGES = INBOUND_STAGES.filter((stage) => stage !== "landed_draft");

/** m4-rulings.md #94: a Method 3 company's received invoice is delivered to its outbox and
    rests at `archived`. The server counts those under this derived key; they are landed. */
const FILE_DELIVERED = "file_delivered";

/** The tab badges read the one stage-counts map rather than three list calls. */
export function tabCounts(counts: StageCounts | undefined): InboundTabCounts {
  const at = (stage: string) => (counts?.[stage] as number | undefined) ?? 0;
  const delivered = at(FILE_DELIVERED);
  return {
    in_flight: IN_FLIGHT_STAGES.reduce((sum, stage) => sum + at(stage), 0) - delivered,
    held: HELD_STAGES.reduce((sum, stage) => sum + at(stage), 0),
    landed: at("landed_draft") + delivered,
    error: at("rejected"),
  };
}

export function useInboundDocuments(state: InboundState, enabled = true) {
  return useInfiniteQuery({
    queryKey: ["documents", "list", { direction: "in", state }],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ direction: "in", state, limit: String(PAGE_SIZE) });
      if (pageParam) params.set("cursor", pageParam);
      return apiGet<Page<InboundDocumentRow>>(`/api/documents?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // Landed and rejected rows do not move; in-flight and held ones do.
    refetchInterval: enabled && (state === "in_flight" || state === "held") ? FAST_POLL : false,
    enabled,
  });
}

export type ResolveAction = "retry" | "reject";

/** The two hold stages; only these accept a resolve (m4-rulings.md #50, #59). */
export const HELD_STAGES = ["held_unrouted", "held_exception"] as const;

export function isHeldStage(stage: string): boolean {
  return (HELD_STAGES as readonly string[]).includes(stage);
}

function invalidateInbound(queryClient: ReturnType<typeof useQueryClient>, docId: string): void {
  queryClient.invalidateQueries({ queryKey: ["documents"] });
  queryClient.invalidateQueries({ queryKey: ["documents", docId] });
  queryClient.invalidateQueries({ queryKey: ["routing", "held"] });
}

/** `POST /api/inbound/{id}/resolve` — 202 DocumentRow, 409 NOT_ON_A_HOLD_STAGE. */
export function useResolveInbound(docId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, note }: { action: ResolveAction; note: string }) =>
      apiSend<InboundDocumentRow>("POST", `/api/inbound/${docId}/resolve`, { action, note }),
    onSuccess: () => invalidateInbound(queryClient, docId),
  });
}

/** `POST /api/inbound/{id}/land` — 202, or 409 NOT_AT_MAPPED / FILE_METHOD_HAS_NO_ERP, or 403. */
export function useLandInbound(docId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiSend<InboundDocumentRow>("POST", `/api/inbound/${docId}/land`),
    onSuccess: () => invalidateInbound(queryClient, docId),
  });
}

export function useInboundStageCounts(): UseQueryResult<StageCounts> {
  return useQuery({
    queryKey: ["documents", "stage-counts", { direction: "in" }],
    queryFn: () => apiGet<StageCounts>("/api/documents/stage-counts?direction=in"),
    refetchInterval: FAST_POLL,
  });
}
